import { CheckOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { calendarColorKeyOf } from "context/InstructorColorProvider";
import { getAllStaffByBusinessId } from "hooks/queries/staff";
import { useMemo } from "react";
import { translate, translateWithVars } from "shared/languages";
import {
  CALENDAR_COLOR_KEYS,
  CALENDAR_COLOR_LABELS,
  calendarPalette,
  CalendarColorKey,
  colors,
} from "shared/theme/color";
import { UserType } from "shared/types/enum";
import { textPx } from "shared/constants/zoom";

/**
 * The instructor's calendar colour, as a row of 14 swatches (a radio group).
 * The chosen one carries a ring and a check, so the choice never rests on
 * colour alone. A colour another active instructor already uses carries a
 * small dot and names them on hover; picking it anyway is allowed (past 14
 * instructors some colours must repeat, and the admin decides who shares).
 * A controlled field for an AntD Form.Item.
 */
export const CalendarColorPicker = ({
  instructorId,
  value,
  onChange,
  disabled = false,
}: {
  instructorId: string;
  value?: string;
  onChange?: (key: CalendarColorKey) => void;
  disabled?: boolean;
}) => {
  const { data: instructors } = getAllStaffByBusinessId(UserType.Instructor, {
    is_active: true,
  });
  const selected = calendarColorKeyOf(instructorId, value);

  // Who else holds each colour, for the dot and its tooltip.
  const usedBy = useMemo(() => {
    const map = new Map<CalendarColorKey, string[]>();
    for (const other of instructors ?? []) {
      if (other.id === instructorId) continue;
      const key = calendarColorKeyOf(other.id, other.staff?.calendar_color);
      map.set(key, [...(map.get(key) ?? []), other.full_name]);
    }
    return map;
  }, [instructors, instructorId]);

  return (
    <div
      role="radiogroup"
      aria-label={translate("label.calendar_color")}
      style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingTop: 4 }}
    >
      {CALENDAR_COLOR_KEYS.map((key) => {
        const colour = calendarPalette[key];
        const isSelected = key === selected;
        const holders = usedBy.get(key);
        const name = CALENDAR_COLOR_LABELS[key];
        const tip = holders?.length
          ? `${name} · ${translateWithVars("label.calendar_color_used_by", {
              name: holders.join(", "),
            })}`
          : name;
        return (
          <Tooltip key={key} title={tip}>
            <button
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={tip}
              disabled={disabled}
              onClick={() => onChange?.(key)}
              className="calendar-color-swatch"
              style={{
                position: "relative",
                width: 30,
                height: 30,
                padding: 0,
                border: "none",
                borderRadius: 999,
                background: colour.accent,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled && !isSelected ? 0.5 : 1,
                boxShadow: isSelected
                  ? `0 0 0 2px ${colors.card}, 0 0 0 4px ${colors.ink}`
                  : undefined,
              }}
            >
              {isSelected && (
                <CheckOutlined style={{ color: "#FFFFFF", fontSize: textPx(14) }} />
              )}
              {holders?.length && !isSelected ? (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    right: -2,
                    bottom: -2,
                    width: 11,
                    height: 11,
                    borderRadius: 999,
                    background: colors.card,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: colors.ink2,
                    }}
                  />
                </span>
              ) : null}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};
